import { useEffect, useCallback, useRef } from 'react';

export const useBookingExpiry = (bookings, onBookingExpired) => {
    const notifiedExpiredBookingIdsRef = useRef(new Set());

    const isBookingExpired = useCallback((booking) => {
        if (!booking?.createdAt && !booking?.createdDateTime) {
            return false;
        }
        
        const bookingCreatedAt = new Date(booking?.createdAt || booking?.createdDateTime);
        const now = new Date();
        const hoursDifference = (now - bookingCreatedAt) / (1000 * 60 * 60);
        
        return hoursDifference > 24;
    }, []);

    const checkExpiredBookings = useCallback(() => {
        if (!bookings?.length) return;

        const expiredBookings = bookings.filter(booking => 
            booking.status === "PENDING" && isBookingExpired(booking)
        );
        const currentExpiredBookingIds = new Set(
            expiredBookings
                .map(booking => booking?.id)
                .filter(Boolean)
        );

        for (const bookingId of notifiedExpiredBookingIdsRef.current) {
            if (!currentExpiredBookingIds.has(bookingId)) {
                notifiedExpiredBookingIdsRef.current.delete(bookingId);
            }
        }

        for (const booking of expiredBookings) {
            const bookingId = booking?.id;
            if (!bookingId || notifiedExpiredBookingIdsRef.current.has(bookingId)) {
                continue;
            }

            notifiedExpiredBookingIdsRef.current.add(bookingId);
            try {
                // Persisting EXPIRED is owned by the backend checkExpiredBookings job.
                onBookingExpired?.(bookingId);
            } catch (error) {
                console.error('Error handling expired booking:', error);
            }
        }
    }, [bookings, isBookingExpired, onBookingExpired]);

    useEffect(() => {
        // Check immediately when component mounts or bookings change
        checkExpiredBookings();

        // Set up interval to check every hour
        const intervalId = setInterval(checkExpiredBookings, 60 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [checkExpiredBookings]);

    return { isBookingExpired };
};
